import { type FC } from 'react';
import { useDispatch } from 'react-redux';
import { useGlobalBoundaryGroups } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { BoundaryTerritoryType } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import {
  activeBTGIdPerBoundaryGroupChanged, boundaryTerritoryGroupsCreateRequest,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionCreators';
import type { BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { useBoundaryToolActiveBTGsSelector } from '~/store/boundaryTerritoryGroups/boundaryTool/boundaryToolBTGs.selectors';
import { boundaryTerritoryGroupHighlightStarted } from '~/store/frontendState/mapTools/boundary/boundarySidebarState/boundarySidebarState.actionCreators';
import {
  mapSettingsBoundariesSetPrimaryBoundaryGroupId, mapSettingsBoundaryGroupOrderGroupHoisted,
} from '~/store/mapSettings/boundaries/mapSettingsBoundaries.actionCreators';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { createBoundaryTerritoryGroupRequestParams } from '../../boundaryTerritoryGroup.helpers';
import { useVerifyNumericFillData } from '../../fill/useVerifyNumericFillData';
import {
  type LoadGlobalBoundaryGroupData, LoadGlobalBoundaryGroupPaneComponent,
} from './loadGlobalBoundaryGroupPane.component';

type LoadGlobalBoundaryGroupPaneContainerProps = Readonly<{
  checkIsNumeric: (columnId: string | null, spreadsheetId: number | null) => Promise<boolean>;
  resetForm: () => void;
}>;

export const LoadGlobalBoundaryGroupPaneContainer: FC<LoadGlobalBoundaryGroupPaneContainerProps> = props => {
  const globalBoundaryGroups = useGlobalBoundaryGroups();
  const clientId = useClientIdSelector();
  const { verifyNumericFillData } = useVerifyNumericFillData({ checkIsNumeric: props.checkIsNumeric });
  const activeBoundaryTerritoryGroups = useBoundaryToolActiveBTGsSelector();
  const mapId = useMapIdSelector();
  const dispatch = useDispatch();

  const handleAddBoundary = (data: LoadGlobalBoundaryGroupData) => {
    if ((mapId === null) || (clientId === null)) {
      return;
    }

    const onSuccess = (boundaryTerritoryGroup: BoundaryTerritoryGroup) => {
      // Set the newly created BTG as active
      dispatch(activeBTGIdPerBoundaryGroupChanged(boundaryTerritoryGroup.boundaryGroupId,
        boundaryTerritoryGroup.boundaryTerritoryGroupId));

      dispatch(mapSettingsBoundaryGroupOrderGroupHoisted(
        boundaryTerritoryGroup.boundaryGroupId,
      ));

      dispatch(mapSettingsBoundariesSetPrimaryBoundaryGroupId(boundaryTerritoryGroup.boundaryGroupId));

      dispatch(boundaryTerritoryGroupHighlightStarted(boundaryTerritoryGroup.boundaryGroupId));
    };

    const createAndDispatch = () => {
      const activeBoundaryTerritoryGroup = activeBoundaryTerritoryGroups
        .find(btg => btg.boundaryGroupId === data.boundaryGroupId);
      const requestParams = createBoundaryTerritoryGroupRequestParams({
        mapId,
        boundaryGroupId: data.boundaryGroupId,
        territoryType: data.boundaryTerritoryType,
        fillData: data.boundaryFillData,
        demographicId: data.demographicId,
        numericCalculateBucketFunction: data.numericCalculateBucketFunction,
      }, activeBoundaryTerritoryGroup);

      dispatch(boundaryTerritoryGroupsCreateRequest(requestParams, onSuccess));
      props.resetForm();
    };

    // Handle numeric territory type with validation
    if (data.boundaryTerritoryType === BoundaryTerritoryType.Numeric) {
      verifyNumericFillData(data.boundaryFillData)
        .then(isNumeric => {
          if (isNumeric && data.boundaryFillData && data.boundaryGroupId !== null) {
            createAndDispatch();
          }
        });
    }
    else {
      // Handle all other territory types
      createAndDispatch();
    }
  };

  return (
    <LoadGlobalBoundaryGroupPaneComponent
      globalBoundaryGroups={globalBoundaryGroups}
      onAddBoundary={handleAddBoundary}
    />
  );
};
